import { Component, Input, OnDestroy } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { tap, filter, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { StyleTypesApiEnum } from '../../../../api/styles/enum/style-types-api.enum';
import { Style } from '../../../Styles/models/style.model';
import { Template } from '../../../Styles/models/template.model';
import { StylesService } from '../../../Styles/services/styles.service';
import { ConfirmDialogComponent } from '../ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';


enum SaveStateEnum {
    new,
    update
}

@Component({
    selector: 'agpr-save-styles-dialog',
    templateUrl: './save-styles-dialog.component.html',
    styleUrls: ['./save-styles-dialog.component.scss'],
    standalone: false
})
export class SaveStylesDialogComponent extends AbstractDialogComponent implements OnDestroy {
    @Input() confirmAction: () => Observable<any>;
    @Input() set type(type: StyleTypesApiEnum) {
        this._type = type;

        if (!!type) {
            this.heading += type;
        }

        this.updateExistingOptions();
    }
    @Input() inputStyle: Style;

    public _type: StyleTypesApiEnum;
    public okLabel: string = 'Styles.saveStylesDialog.saveLabel';
    public heading: string = 'Styles.saveStylesDialog.header';

    public saveType: SaveStateEnum = SaveStateEnum.new;
    public stylesList: Style[];
    public existingOptions: Style[];
    public existingStyleDropdownOptions: DropdownOptionModel[];
    public newStyleName: string = '';
    public formValid: boolean = false;
    public existingStyleModel: Style;
    public selectedStyleOption: DropdownOptionModel;
    public showError: boolean = false;
    public duplicateStyleName: string;

    private subscriptions: Subscription[] = [];
    private isSaving: boolean = false;

    readonly id = 'SaveStylesDialog';
    readonly buttonTypes = ButtonTypesEnum;
    readonly saveStateType = SaveStateEnum;

    constructor(
        public coreDialogService: CoreDialogService,
        private stylesService: StylesService,
        private localizationService: LocalizationService,
        private messageService: MessageService
    ) {
        super(coreDialogService, 'SaveStyles');

        this.subscriptions.push(
            this.stylesService.all$.subscribe(
                styles => {
                    this.stylesList = styles;

                    this.updateExistingOptions();
                }
            )
        );

        this.validate();
    }


    ok() {
        if (this.isSaving) {
            return;
        }

        const styleToSave = this.inputStyle.clone();
        styleToSave.type = this._type;
        styleToSave.style.defaultReference = styleToSave.style.getDefaultReference();
        delete (styleToSave.style as any).styleState;
        this.showError = false;

        switch (this.saveType) {
            case SaveStateEnum.new: {
                this.stylesService.isNameDuplicated$(this.newStyleName).pipe(
                    tap(isDuplicate => {
                        if (isDuplicate) {
                            this.duplicateStyleName = this.newStyleName;
                            this.showError = true;

                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Styles.manageDialog.error.duplicateName.heading',
                                detail: this.localizationService.transform('Styles.manageDialog.error.duplicateName.message', {
                                    name: this.newStyleName
                                })
                            });
                        }
                    }),
                    filter(x => !x),
                    switchMap(() => {
                        this.isSaving = true;
                        styleToSave.name = this.newStyleName;
                        if (styleToSave.template) {
                            styleToSave.template.id = null;
                        }

                        return this.stylesService.create(styleToSave);
                    })
                ).subscribe((newId) => {
                    if (newId) {
                        this.closeDialog(newId);
                    }
                    this.isSaving = false;
                });

                break;
            }
            case SaveStateEnum.update: {
                this.handleStyleUpdate(styleToSave);
                break;
            }
        }
    }

    private handleStyleUpdate(styleToSave: Style) {
        if (this.existingStyleModel.template) {
            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'Styles.overWriteStyleConfirmationDialog',
                    heading: 'Styles.overWriteStyleConfirmationDialog.heading',
                    alertMessage: 'Styles.overWriteStyleConfirmationDialog.infoMessage',
                    alertType: messageSeverityEnum.warning,
                    message: 'Styles.overWriteStyleConfirmationDialog.message',
                    okLabel: 'Styles.overWriteStyleConfirmationDialog.okButtonLabel'
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            styleToSave.id = this.existingStyleModel.id;
                            styleToSave.name = this.existingStyleModel.name;
                            if (this.existingStyleModel.template) {
                                if (!styleToSave.template) {
                                    // New blocks may not have a template
                                    styleToSave.template = new Template();
                                }
                                styleToSave.template.id = this.existingStyleModel.template.id;
                            }
                            this.stylesService.update(styleToSave).subscribe({
                                next: () => this.closeDialog(styleToSave.id),
                                error: () => {}
                            });
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);

            return;
        }

        styleToSave.id = this.existingStyleModel.id;
        styleToSave.name = this.existingStyleModel.name;

        this.stylesService.update(styleToSave).subscribe({
            next: () => this.closeDialog(styleToSave.id),
            error: () => {}
        });
    }


    cancel() {
        this.closeDialog(false);
    }

    public saveTypeUpdated(event: any): void {
        if (this.saveType === SaveStateEnum.new) {
            this.okLabel = 'Styles.saveStylesDialog.saveLabel';
        } else if (this.saveType === SaveStateEnum.update) {
            this.okLabel = 'Styles.saveStylesDialog.updateLabel';
            this.showError = false;
        }

        this.validate();
    }

    public validate(): void {
        if (this.saveType === SaveStateEnum.new) {
            this.formValid = this.newStyleName.length > 0;
        } else if (this.saveType === SaveStateEnum.update) {
            this.formValid = !!this.existingStyleModel;
        }
    }

    onDropdownChanged(style: Style): void {
        this.existingStyleModel = style;
        this.validate();
    }

    private updateExistingOptions(): void {
        if (!this._type || !this.stylesList) {
            return;
        }

        this.existingOptions = this.stylesList.filter(style => style.type === this._type);

        this.existingStyleDropdownOptions = this.existingOptions?.map(x => ({ label: x.name, value: x})) || [];
        this.existingStyleDropdownOptions?.sort((a, b) => a.label.localeCompare(b.label));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
